// src/oura/components/site/ClientReadersCard.tsx
//
// AI readers on the client's Website page: the same three results staff see on
// Site (ChatGPT, Claude, Perplexity), read-only and in plain words. Staff read
// "robots.txt allows it · got in · words visible without scripts"; the client
// reads "Allowed in · Read your pages · Saw your words". A reader that cannot
// get in is amber, not the blue staff use for work in progress. No test
// button and no Delivery item: staff run the test, the client reads the
// latest result. Renders nothing until a result exists for this login to read
// (getReaderChecks); a read that failed says so, so an outage never looks
// like a check that has not run.

import { useEffect, useState } from "react";
import { BotIcon } from "lucide-react";
import { getReaderChecks, shortDay, type ReaderCheck, type ReaderChecksRead } from "@service/aeo/delivery";
import { myDate } from "@service/aeo/clientDocsRules";
import { clientArea } from "../../merchantV2";
import { cn } from "../../lib/cn";
import { Dot, FoldCard, ListRow, TYPE, type Tone } from "@oura/ui";
import { readerPasses } from "../../delivery/workModel";

const NAME: Record<ReaderCheck["reader"], string> = { chatgpt: "ChatGPT", claude: "Claude", perplexity: "Perplexity" };

/** What happened when this AI tried to read the site, one short fact each. */
function plainFacts(r: ReaderCheck): string {
    const allowed = r.robotsAllows == null ? null : r.robotsAllows ? "Allowed in" : "Blocked by your robots.txt file";
    const read = r.gotIn == null ? null : r.gotIn ? "Read your pages" : "Turned away";
    const words = r.gotIn !== true || r.wordsVisible == null ? null : r.wordsVisible ? "Saw your words" : "Saw a blank page";
    return [allowed, read, words].filter(Boolean).join(" · ") || "Not checked yet";
}

function toneOf(r: ReaderCheck): Tone {
    if (r.checkedAt == null) return "gray";
    return readerPasses(r) ? "green" : "amber";
}

export function ClientReadersCard({ businessId }: { businessId: string }) {
    // The result is kept with the business it was read for, and shown only
    // for that business: on a change of business the first render shows
    // nothing rather than the last business's readers (Jack, #4228).
    const [stored, setStored] = useState<{ businessId: string; value: ReaderChecksRead } | null>(null);
    const read = stored?.businessId === businessId ? stored.value : null;
    useEffect(() => {
        let cancelled = false;
        void getReaderChecks(businessId).then(
            (result) => {
                if (!cancelled) setStored({ businessId, value: result });
            },
            () => {
                if (!cancelled) setStored({ businessId, value: { kind: "error" } });
            },
        );
        return () => {
            cancelled = true;
        };
    }, [businessId]);
    if (!read || read.kind === "none") return null;
    const head = { id: "client-ai-readers", anchor: "ai-readers", className: "scroll-mt-6", title: "Can AI read your site", icon: { icon: BotIcon, area: clientArea("site") } };
    if (read.kind === "error") {
        return (
            <FoldCard {...head} foldedFact="Not loaded">
                <p role="alert" className={cn(TYPE.tb, "m-0 text-gray-600")}>
                    We could not load the latest AI reader check. Reload to try again.
                </p>
            </FoldCard>
        );
    }
    const { checks } = read;
    const pass = checks.filter(readerPasses).length;
    return (
        <FoldCard {...head} right={`${pass} of ${checks.length} can`} foldedFact={`${pass} of ${checks.length} can`}>
            <div>
                {checks.map((r) => (
                    <ListRow
                        key={r.reader}
                        lead="dot"
                        icon={<Dot tone={toneOf(r)} />}
                        title={NAME[r.reader]}
                        sub={plainFacts(r)}
                        meta={r.checkedAt ? `Checked ${shortDay(myDate(r.checkedAt))}` : undefined}
                        muted={r.checkedAt == null}
                        stackMeta
                    />
                ))}
            </div>
        </FoldCard>
    );
}
